/**
 * This browser's device identifier.
 *
 * Once an administrator switches on device authorization, only approved machines may
 * use the system. A machine identifies itself with this value: a random token generated
 * once, kept in localStorage and sent as the X-Device-Id header on every API request
 * (see src/api/deviceHeader.ts), or as ?device= on report links.
 *
 * It is an identifier, not a credential: it grants nothing without a valid login.
 * Clearing browser data creates a new one, which the server sees as a new machine
 * awaiting approval -- by design.
 */
const STORAGE_KEY = 'device_id'

function generate(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16)
    crypto.getRandomValues(bytes)
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
}

export function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(STORAGE_KEY)
    if (existing) return existing
    const created = generate()
    localStorage.setItem(STORAGE_KEY, created)
    return created
  } catch {
    // storage blocked: every request then looks like a new, unapproved machine
    return generate()
  }
}

/** The 8 characters an administrator sees in the device list, to read out over a phone. */
export function getDeviceCode(): string {
  return getDeviceId().slice(0, 8)
}

/**
 * A report address that carries this machine's id and the sign-in ticket. The PDF viewer
 * and CSV links cannot send headers, so both ride in the query; the server refuses a
 * report without a live ticket.
 */
export function withDevice(url: string): string {
  let ticket = ''
  try {
    ticket = localStorage.getItem('authTicket') ?? ''
  } catch {
    /* storage can be unavailable */
  }
  return (
    url +
    (url.includes('?') ? '&' : '?') +
    'device=' +
    encodeURIComponent(getDeviceId()) +
    (ticket ? '&ticket=' + encodeURIComponent(ticket) : '')
  )
}
